import { useMemo, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { PageHeader } from "@/components/PageHeader";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { formatShortDateRange } from "@/lib/date-utils";
import { Clock, FolderOpen, CalendarDays, Search } from "lucide-react";
import { useLastOpenedProjects } from "@/hooks/useLastOpenedProjects";
import {
  useProjects,
  useMyUpcomingProjects,
  ProjectWithRelations,
} from "@/hooks/useProjects";
import { useAuth } from "@/hooks/useAuth";
import { Input } from "@/components/ui/input";
import { formatProjectName } from "@/lib/format-project-name";
import { matchesSearchTokens } from "@/lib/search-utils";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandList,
} from "@/components/ui/command";

function ProjectRow({ project }: { project: ProjectWithRelations }) {
  return (
    <Link
      to={`/projects/${project.id}`}
      className="flex items-center gap-3 px-3 py-2 text-inherit no-underline hover:bg-muted/50 transition-colors"
    >
      {project.number && (
        <span className="text-xs text-muted-foreground font-mono shrink-0">
          {project.number}
        </span>
      )}
      <span className="text-sm font-medium truncate">
        {formatProjectName(project.name, project.event_start_date)}
      </span>
      {project.companies?.name && (
        <span className="text-xs text-muted-foreground truncate hidden sm:inline">
          {project.companies.name}
        </span>
      )}
      <span className="ml-auto flex items-center gap-2 shrink-0">
        <span className="hidden sm:inline text-xs text-muted-foreground whitespace-nowrap">
          {formatShortDateRange(
            project.event_start_date ?? project.start_date,
            project.event_end_date ?? project.end_date,
          )}
        </span>
      </span>
    </Link>
  );
}

export default function DashboardPage() {
  const navigate = useNavigate();
  const { personId } = useAuth();
  const { lastOpenedIds } = useLastOpenedProjects();
  const { data: projects, isLoading } = useProjects(false);
  const { data: upcomingProjects, isLoading: isLoadingUpcoming } =
    useMyUpcomingProjects(personId);

  const [search, setSearch] = useState("");

  const recentlyOpenedProjects = useMemo(() => {
    if (!projects || lastOpenedIds.length === 0) return [];
    const projectMap = new Map(projects.map((p) => [p.id, p]));
    return lastOpenedIds
      .map((id) => projectMap.get(id))
      .filter((p): p is ProjectWithRelations => p != null && !p.deleted_at);
  }, [projects, lastOpenedIds]);

  const searchResults = useMemo(() => {
    if (!search.trim() || !projects) return [];
    const q = search.toLowerCase();
    return projects
      .filter((p) => {
        if (p.deleted_at) return false;

        const searchText = [
          p.name ?? "",
          formatProjectName(p.name, p.event_start_date),
          p.number?.toString() ?? "",
        ]
          .join(" ")
          .toLowerCase();

        return matchesSearchTokens(searchText, q);
      })
      .slice(0, 8);
  }, [search, projects]);

  return (
    <div className="space-y-6 animate-fade-in">
      <PageHeader title="Dashboard" />

      {/* Quick Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Quick search projects..."
          className="pl-9"
        />
        {search.trim() && (
          <div className="absolute z-50 mt-1 w-full rounded-md border bg-popover shadow-md">
            <Command>
              <CommandList>
                {searchResults.length === 0 ? (
                  <CommandEmpty>No projects found.</CommandEmpty>
                ) : (
                  <CommandGroup>
                    {searchResults.map((project) => (
                      <CommandItem
                        key={project.id}
                        onSelect={() => {
                          setSearch("");
                          navigate(`/projects/${project.id}`);
                        }}
                        className="cursor-pointer"
                      >
                        <div className="flex items-center gap-3 w-full">
                          {project.number && (
                            <span className="text-xs text-muted-foreground font-mono shrink-0">
                              {project.number}
                            </span>
                          )}
                          <span className="text-sm font-medium truncate">
                            {formatProjectName(
                              project.name,
                              project.event_start_date,
                            )}
                          </span>
                        </div>
                      </CommandItem>
                    ))}
                  </CommandGroup>
                )}
              </CommandList>
            </Command>
          </div>
        )}
      </div>

      {/* My Upcoming Projects */}
      <div className="space-y-3">
        <div className="flex items-center gap-2">
          <CalendarDays className="h-4 w-4 text-muted-foreground" />
          <h2 className="text-sm font-medium text-muted-foreground">
            My Upcoming Projects
          </h2>
        </div>

        {isLoadingUpcoming ? (
          <div className="space-y-2">
            {[1, 2, 3].map((i) => (
              <Skeleton key={i} className="h-10 w-full" />
            ))}
          </div>
        ) : !upcomingProjects || upcomingProjects.length === 0 ? (
          <Card>
            <CardContent className="p-8 text-center">
              <CalendarDays className="h-10 w-10 mx-auto text-muted-foreground mb-3" />
              <h3 className="text-sm font-medium text-foreground mb-1">
                No upcoming projects
              </h3>
              <p className="text-sm text-muted-foreground">
                You are not planned on any upcoming projects.
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="divide-y rounded-md border">
            {upcomingProjects.map((item) => (
              <ProjectRow key={item.project.id} project={item.project} />
            ))}
          </div>
        )}
      </div>

      {/* Recently Opened */}
      <div className="space-y-3">
        <div className="flex items-center gap-2">
          <Clock className="h-4 w-4 text-muted-foreground" />
          <h2 className="text-sm font-medium text-muted-foreground">
            Recently Opened
          </h2>
        </div>

        {isLoading ? (
          <div className="space-y-2">
            {[1, 2, 3].map((i) => (
              <Skeleton key={i} className="h-10 w-full" />
            ))}
          </div>
        ) : recentlyOpenedProjects.length === 0 ? (
          <Card>
            <CardContent className="p-8 text-center">
              <FolderOpen className="h-10 w-10 mx-auto text-muted-foreground mb-3" />
              <h3 className="text-sm font-medium text-foreground mb-1">
                No recently opened projects
              </h3>
              <p className="text-sm text-muted-foreground">
                Projects you open will appear here.{" "}
                <Link to="/projects" className="text-primary hover:underline">
                  Browse all projects
                </Link>
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="divide-y rounded-md border">
            {recentlyOpenedProjects.map((project) => (
              <ProjectRow key={project.id} project={project} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
